import type { ReactNode } from 'react';
import type { AssistantToolResult } from '@cumpli/shared';
import { RiskCard } from './risk-card';
import { MissingDocsCard } from './missing-docs-card';
import { ScreeningCard } from './screening-card';
import { ClientsCard } from './clients-card';
import { SummaryCard } from './summary-card';
import { AlertsCard } from './alerts-card';
import { CapabilitiesCard } from './capabilities-card';

/**
 * Renders the rich answer cards attached to an assistant turn. Each
 * `AssistantToolResult` is a discriminated union tagged by `tool`, so the
 * switch narrows `r.data` to the right shape for its card; an unrecognized tool
 * renders nothing (forward-compatible). Reusable in the drawer and, later, a
 * full-page view.
 *
 * `variant` (from <Thread>) tunes the capability cards' density; `onSeedComposer`
 * lets an interactive card seed the surface's composer with a picked prompt (no
 * auto-send). `animate` is true only on the just-arrived turn: it staggers the
 * cards' entrance (index*80ms) and drives the count-up / risk-ring / screening
 * sweep animations. Under reduced motion the parent passes `animate={false}`, so
 * everything renders its final state at once.
 */

/** Wrap a card so it rises+fades in on a staggered delay when the turn is fresh. */
function CardSlot({ animate, index, children }: { animate: boolean; index: number; children: ReactNode }) {
  if (!animate) return <>{children}</>;
  return (
    <div
      className="motion-safe:animate-slide-up"
      style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'both' }}
    >
      {children}
    </div>
  );
}

export function ToolResultCards({
  results,
  variant,
  animate = false,
  onSeedComposer,
}: {
  results?: AssistantToolResult[] | undefined;
  variant?: 'drawer' | 'page' | undefined;
  animate?: boolean | undefined;
  onSeedComposer?: ((prompt: string) => void) | undefined;
}) {
  if (!results || results.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {results.map((r, i) => {
        let card: ReactNode = null;
        switch (r.tool) {
          case 'explain_client_risk':
            card = <RiskCard data={r.data} animate={animate} />;
            break;
          case 'list_missing_documents':
            card = <MissingDocsCard data={r.data} />;
            break;
          case 'screen_name':
            card = <ScreeningCard data={r.data} justRan={animate} />;
            break;
          case 'find_client':
            card = <ClientsCard rows={r.data.matches} count={r.data.count} variant="find" />;
            break;
          case 'list_clients':
            card = <ClientsCard rows={r.data.clients} count={r.data.count} variant="list" />;
            break;
          case 'get_dashboard_summary':
            card = <SummaryCard data={r.data} animate={animate} />;
            break;
          case 'list_alerts':
            card = <AlertsCard data={r.data} />;
            break;
          case 'list_capabilities':
            card = (
              <CapabilitiesCard tools={r.data.tools} variant={variant} onPick={onSeedComposer} />
            );
            break;
          default:
            return null;
        }
        return (
          <CardSlot key={i} animate={animate} index={i}>
            {card}
          </CardSlot>
        );
      })}
    </div>
  );
}
